import { Button, Dropdown, Space, message, notification } from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
  ApiService,
  BaseRequestStatus,
  DocumentFormat,
  RequestDocuments,
  RequestStatus,
  SystemActionsEnum,
} from "../api";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import {
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileDoneOutlined,
  HistoryOutlined,
  QrcodeOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DOMAIN, NotificationType } from "../constants";
import { downloadFile } from "../api/downloadFile";
import DocumentPreview from "./DocumentPreview";
import HandoverModal from "./HandoverModal";
import ExtendModal from "./ExtendModal";
import CaseTimelineModal from "./CaseTimelineModal";
import QrCodeModal from "./QrCodeModal";
import WithdrawModal from "./WithdrawModal";

type ActionsDropdownProps = {
  id?: string;
  number?: string;
  status?: RequestStatus;
  /** Whether this person may call the case off, and what the template calls
   *  its cancelled state. Computed by the server (`transitions.can_withdraw`)
   *  so the menu cannot offer something the endpoint would refuse. `null` on a
   *  payload that did not compute it — the entry is simply not shown. */
  withdraw?: { allowed: boolean; reason: string | null; status_name: string | null } | null;
  //refreshRequest: () => void;
  fetch: number;
  setFetch: (fetch: number) => void;
};

/** Above this many documents the menu nests them instead of listing every
 *  document × action at the top level. One document must not become worse to
 *  reach than it was when a template could only have one — so one stays flat
 *  (Preview, then a Generate per format) and anything more groups by document.
 *
 *  It was 2, which was right while a document offered only downloads. Preview
 *  adds a third entry apiece, and six top-level entries is precisely the
 *  burying of the status actions this limit exists to prevent. */
const FLAT_DOCUMENT_LIMIT = 1;

const systemActionsOf = (status?: RequestStatus): Array<SystemActionsEnum> => {
  const raw = status?.system_actions;
  return (Array.isArray(raw) ? raw : raw ? [raw] : []) as Array<SystemActionsEnum>;
};

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  id,
  number,
  status,
  withdraw,
  fetch,
  setFetch,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [actions, setActions] = useState<ItemType[]>([]);
  const [handoverModalVisible, setHandoverModalVisible] =
    useState<boolean>(false);
  const [extendModalVisible, setExtendModalVisible] = useState<boolean>(false);
  const [auditModalVisible, setAuditModalVisible] = useState<boolean>(false);
  const [qrModalVisible, setQrModalVisible] = useState<boolean>(false);
  const [withdrawModalVisible, setWithdrawModalVisible] =
    useState<boolean>(false);
  // Which documents this permit can print. A template carries several — the
  // permit, a government form, an annex — so the menu can't be built from the
  // status alone.
  const [documents, setDocuments] = useState<RequestDocuments | undefined>();
  // Both the status actions and the document downloads are slow enough to look
  // like nothing happened; the button spins for the duration of either.
  const [busy, setBusy] = useState<boolean>(false);
  // Which document is on screen, if any. Held rather than derived so the modal
  // keeps its title while it closes.
  const [preview, setPreview] = useState<{ id: number; name: string } | null>(
    null,
  );

  // These endpoints are JWT-protected, so they can't be opened by navigation.
  const download = async (url: string, fallbackName: string) => {
    setBusy(true);
    try {
      await downloadFile(url, fallbackName);
    } catch (error: any) {
      message.error(error?.message || t("Download failed"));
    } finally {
      setBusy(false);
    }
  };

  const formatIcon = (fmt: DocumentFormat) =>
    fmt === "pdf" ? <FilePdfOutlined /> : <FileWordOutlined />;

  /** "Gas Test Report (PDF)" — the document's own name, then the format. */
  const documentLabel = (name: string, fmt: DocumentFormat) =>
    `${name} (${fmt.toUpperCase()})`;

  const downloadDocument = (
    documentId: number,
    name: string,
    fmt: DocumentFormat
  ) =>
    download(
      `${DOMAIN}/api/main/download-document/${id}/${documentId}/?as=${fmt}`,
      `${number ?? id} - ${name}.${fmt}`
    );

  /** One entry per document per format, from what the server says it can print. */
  const populateDocumentActions = (): ItemType[] => {
    if (!documents) {
      return [
        {
          label: t("Loading documents…"),
          key: "documents-loading",
          icon: <FileTextOutlined />,
          disabled: true,
        },
      ];
    }

    if (documents.documents.length === 0) {
      // Better than an entry that downloads nothing: it says who can fix it.
      return [
        {
          label: t("No documents are set up for this permit type"),
          key: "documents-none",
          icon: <FileTextOutlined />,
          disabled: true,
        },
      ];
    }

    /** Preview first: it is the reversible one, and the one people want most.
     *  Offered only where the server can make a PDF — the preview renders a
     *  PDF, and a .docx is not something a browser can draw. */
    const entries = (documentId: number, name: string) => [
      ...(documents.pdf_available
        ? [
            {
              label: t("Preview {{name}}", { name }),
              key: `document-${documentId}-preview`,
              icon: <EyeOutlined />,
              onClick: () => setPreview({ id: documentId, name }),
            },
          ]
        : []),
      ...documents.formats.map((fmt) => ({
        label: documentLabel(name, fmt),
        key: `document-${documentId}-${fmt}`,
        icon: formatIcon(fmt),
        onClick: () => downloadDocument(documentId, name, fmt),
      })),
    ];

    if (documents.documents.length <= FLAT_DOCUMENT_LIMIT) {
      return documents.documents.flatMap((doc) =>
        entries(doc.id, doc.name).map((entry) =>
          entry.key.endsWith("-preview")
            ? entry
            : {
                ...entry,
                label: t("Generate {{label}}", { label: entry.label }),
              }
        )
      );
    }

    // Enough documents that listing every pair would bury the status actions.
    return [
      {
        label: t("Documents"),
        key: "documents",
        icon: <FileTextOutlined />,
        children: documents.documents.map((doc) => ({
          key: `document-${doc.id}`,
          type: "group" as const,
          label: doc.name,
          children: entries(doc.id, doc.name),
        })),
      },
    ];
  };

  const populateSystemActions = (status?: RequestStatus) => {
    const systemActions = systemActionsOf(status);

    let requestActions: ItemType[] = [];
    systemActions.forEach((sa) => {
      switch (sa) {
        case SystemActionsEnum.EDIT:
          requestActions.push({
            label: t("Edit"),
            icon: <EditOutlined />,
            key: sa.toString(),
            onClick: () => navigate(`/requests/edit/${id}`),
          });
          break;
        case SystemActionsEnum.DOWNLOAD_DOCUMENT:
          requestActions.push(...populateDocumentActions());
          break;
        case SystemActionsEnum.DOWNLOAD_CMS:
          requestActions.push({
            label: t("Download CMS"),
            key: sa.toString(),
            icon: <FileDoneOutlined />,
            onClick: () =>
              download(
                `${DOMAIN}/api/main/get-request-cms/${id}/`,
                `${number ?? id}.cms`,
              ),
          });
          break;
        case SystemActionsEnum.HANDOVER:
          requestActions.push({
            label: t("Handover"),
            key: sa.toString(),
            onClick: () => setHandoverModalVisible(true),
          });
          break;
        case SystemActionsEnum.EXTEND:
          requestActions.push({
            label: t("Extend"),
            key: sa.toString(),
            onClick: () => setExtendModalVisible(true),
          });
          break;
      }
    });

    return requestActions;
  };

  const handlePerformAction = async (actionId: number) => {
    setBusy(true);
    try {
      await ApiService.apiMainPerformActionCreate({
        request_id: parseInt(id!),
        status_id: actionId,
      });
      openNotificationWithIcon("success");
      setFetch(fetch + 1);
    } catch (e: any) {
      // The backend rejects e.g. sending for approval with empty approver slots
      // (400 {message, missing:[...]}). Show that instead of a generic error so
      // the user knows exactly what to fix.
      const body = e?.body;
      const missing: string[] = Array.isArray(body?.missing) ? body.missing : [];
      const detail: string =
        body?.message || body?.detail || t("Something went wrong.");
      notification.error({
        message: t("Action could not be completed"),
        description: missing.length ? (
          <div>
            <div>{detail}</div>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {missing.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          </div>
        ) : (
          detail
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  const openNotificationWithIcon = (type: NotificationType) => {
    if (type === "success") {
      notification[type]({
        message: t("Success"),
        description: t("Request has been successfully processed."),
      });
    } else {
      notification[type]({
        message: t("Error"),
        description: t("Something went wrong."),
      });
    }
  };

  const populateOtherActions = (status?: RequestStatus) => {
    const otherActions: Array<any> = status?.next_statuses ?? [];

    let statusActions: ItemType[] = [];
    otherActions.forEach((action: BaseRequestStatus) => {
      statusActions.push({
        label: action.action_title,
        key: action.id,
        onClick: () => handlePerformAction(action.id),
      });
    });

    return statusActions;
  };

  const auditAction: ItemType = {
    label: t("What happened here"),
    key: "audit-trail",
    icon: <HistoryOutlined />,
    onClick: () => setAuditModalVisible(true),
  };

  const qrAction: ItemType = {
    label: t("Generate QR"),
    key: "generate-qr",
    icon: <QrcodeOutlined />,
    onClick: () => setQrModalVisible(true),
  };

  /* Last in the menu, danger-coloured, and behind a dialog that asks for a
     reason. It is the only entry here that ends a case, and it sits nowhere
     near the entries that print one. */
  const withdrawAction: ItemType | null = withdraw?.allowed
    ? {
        label: t("Withdraw this case"),
        key: "withdraw",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => setWithdrawModalVisible(true),
      }
    : null;

  // Only worth a round trip on a status that actually offers the download.
  useEffect(() => {
    if (!id || !systemActionsOf(status).includes(SystemActionsEnum.DOWNLOAD_DOCUMENT)) {
      return;
    }
    let cancelled = false;
    ApiService.apiMainRequestDocumentsRetrieve(parseInt(id))
      .then((data) => {
        if (!cancelled) setDocuments(data);
      })
      .catch(() => {
        // A failed lookup leaves the menu saying there is nothing to print,
        // which is the truthful outcome — the download would fail too.
        if (!cancelled) {
          setDocuments({ documents: [], formats: ["docx"], pdf_available: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id, status]);

  useEffect(() => {
    setActions([
      qrAction,
      auditAction,
      ...populateSystemActions(status),
      ...populateOtherActions(status),
      ...(withdrawAction ? [{ type: "divider" } as ItemType, withdrawAction] : []),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, documents, withdraw]);

  if (status === undefined) {
    return <></>;
  }

  return (
    <>
      <Dropdown menu={{ items: actions }} disabled={busy}>
        <Button type="primary" loading={busy}>
          <Space>
            {t("Actions")}
            <DownOutlined />
          </Space>
        </Button>
      </Dropdown>
      <HandoverModal
        fetch={fetch}
        setFetch={setFetch}
        requestId={id!}
        visible={handoverModalVisible}
        setVisible={setHandoverModalVisible}
      />
      <WithdrawModal
        fetch={fetch}
        setFetch={setFetch}
        requestId={id!}
        statusName={withdraw?.status_name}
        visible={withdrawModalVisible}
        setVisible={setWithdrawModalVisible}
      />
      <ExtendModal
        fetch={fetch}
        setFetch={setFetch}
        requestId={id!}
        visible={extendModalVisible}
        setVisible={setExtendModalVisible}
      />
      {/* The merged story, at the entry point people already used to look for
          it. See CaseTimelineModal for why this replaces the dialog rather
          than sitting beside it. */}
      <CaseTimelineModal
        id={id}
        number={number}
        visible={auditModalVisible}
        setVisible={setAuditModalVisible}
      />
      <QrCodeModal
        requestId={id}
        number={number}
        visible={qrModalVisible}
        setVisible={setQrModalVisible}
      />
      {/* Same endpoint as the download, one query parameter apart — so the
          preview cannot disagree with what a download would hand over about
          permissions, freshness, or which document is which. */}
      {preview && (
        <DocumentPreview
          open
          onClose={() => setPreview(null)}
          title={preview.name}
          url={`${DOMAIN}/api/main/download-document/${id}/${preview.id}/?as=pdf&disposition=inline`}
          downloadName={`${number ?? id} - ${preview.name}.pdf`}
          fallback={{
            label: t("Download the DOCX instead"),
            onClick: () => downloadDocument(preview.id, preview.name, "docx"),
          }}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
